import React, { useEffect, useRef, useState } from 'react';
import { formatDateTimeOrdinal } from './date_util';
import { loadGoogleMapsScript } from './maps_loader';

function isDateValue(val) {
  if (!val || typeof val !== 'string') return false;
  return !isNaN(new Date(val).getTime());
}

function getPinIcon(color) {
  return {
    url: 'data:image/svg+xml,' + encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="36">
        <path d="M12 0C5.37 0 0 5.37 0 12c0 9 12 24 12 24s12-15 12-24c0-6.63-5.37-12-12-12z" fill="${color}"/>
        <circle cx="12" cy="12" r="5" fill="white"/>
      </svg>`
    ),
    size: new window.google.maps.Size(24, 36),
    anchor: new window.google.maps.Point(12, 36),
  };
}

export const ShiftMap = ({ shifts, defaultColor, onShiftClick }) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!shifts || shifts.length === 0) return;

    let cancelled = false;

    const initMap = async () => {
      try {
        await loadGoogleMapsScript();
      } catch (e) {
        if (!cancelled) setLoadError(e.message);
        return;
      }

      if (cancelled) return;

      if (!mapInstance.current && mapRef.current) {
        mapInstance.current = new window.google.maps.Map(mapRef.current, {
          center: { lat: 39.8283, lng: -98.5795 },
          zoom: 4,
          styles: [
            {
              "featureType": "all",
              "elementType": "labels.text.fill",
              "stylers": [{ "color": "#7c93a3" }, { "lightness": "-10" }]
            },
            {
              "featureType": "administrative.country",
              "elementType": "geometry",
              "stylers": [{ "visibility": "on" }]
            },
            {
              "featureType": "administrative.country",
              "elementType": "geometry.stroke",
              "stylers": [{ "color": "#a0a4a5" }]
            },
            {
              "featureType": "administrative.province",
              "elementType": "geometry.stroke",
              "stylers": [{ "color": "#bfe1f1" }]
            },
            {
              "featureType": "landscape",
              "elementType": "geometry",
              "stylers": [{ "color": "#e3ebef" }]
            },
            {
              "featureType": "landscape.natural",
              "elementType": "labels",
              "stylers": [{ "visibility": "off" }]
            },
            {
              "featureType": "poi",
              "elementType": "all",
              "stylers": [{ "visibility": "off" }]
            },
            {
              "featureType": "road",
              "elementType": "all",
              "stylers": [{ "color": "#d3dae0" }]
            },
            {
              "featureType": "road",
              "elementType": "labels",
              "stylers": [{ "visibility": "simplified" }]
            },
            {
              "featureType": "road.highway",
              "elementType": "geometry",
              "stylers": [{ "color": "#bcceda" }]
            },
            {
              "featureType": "road.highway",
              "elementType": "labels.text.fill",
              "stylers": [{ "color": "#6d7f8b" }]
            },
            {
              "featureType": "road.arterial",
              "elementType": "geometry",
              "stylers": [{ "color": "#d3dae0" }]
            },
            {
              "featureType": "road.local",
              "elementType": "geometry",
              "stylers": [{ "color": "#e7eef2" }]
            },
            {
              "featureType": "transit",
              "elementType": "all",
              "stylers": [{ "visibility": "off" }]
            },
            {
              "featureType": "water",
              "elementType": "geometry",
              "stylers": [{ "color": "#a6cbe3" }]
            },
            {
              "featureType": "water",
              "elementType": "labels.text.fill",
              "stylers": [{ "color": "#6d8191" }]
            }
          ]
        });
      }

      // Remove markers from previous render
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];

      const geocoder = new window.google.maps.Geocoder();
      const addressesToGeocode = shifts.filter(
        (shift) => shift && shift[898] && shift[898].value
      );

      if (addressesToGeocode.length === 0) return;

      addressesToGeocode.forEach((shift) => {
        const address = shift[898].value;
        const clientName = shift[378] ? shift[378].value : '';
        const locationCode = shift[321] ? shift[321].value : '';
        const formatTime = formatDateTimeOrdinal;
        const startTime = shift[6] ? formatTime(shift[6].value) : '';
        const endTime = shift[7] ? formatTime(shift[7].value) : '';

        geocoder.geocode({ address }, (results, status) => {
          if (cancelled) return;

          if (status === 'OK') {
            const position = results[0].geometry.location;

            const clockedIn = shift[425] && isDateValue(shift[425].value);
            const shiftStart = shift[6] && shift[6].value ? new Date(shift[6].value) : null;
            let pinColor;
            if (clockedIn) {
              pinColor = 'limegreen';
            } else if (shiftStart && new Date() > shiftStart) {
              pinColor = 'orange';
            } else {
              pinColor = '#B7966A';
            }

            const marker = new window.google.maps.Marker({
              position,
              map: mapInstance.current,
              title: `${clientName} - ${locationCode}`,
              icon: getPinIcon(pinColor),
            });

            const infoWindow = new window.google.maps.InfoWindow({
              content: `
                <div style="font-size:13px; max-width:200px;">
                  <strong>${clientName} &ndash; ${locationCode}</strong><br/>
                  <hr style="margin:4px 0;"/>
                  <b>Address:</b> ${address}<br/>
                  <b>Start:</b> ${startTime}<br/>
                  <b>End:</b> ${endTime}
                </div>
              `,
            });

            marker.addListener('mouseover', () => {
              infoWindow.open({ map: mapInstance.current, anchor: marker });
            });

            marker.addListener('mouseout', () => {
              infoWindow.close();
            });

            marker.addListener('click', () => {
              if (onShiftClick) onShiftClick(shift);
            });

            markersRef.current.push(marker);
          }
        });
      });
    };

    initMap();

    return () => { cancelled = true; };
  }, [shifts, defaultColor]);

  if (loadError) {
    return (
      <div style={{ width: '100%', height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', borderRadius: '8px', color: '#666', fontSize: '14px' }}>
        Map unavailable — {loadError}
      </div>
    );
  }

  const scheduledColor = '#B7966A';
  const legendItems = [
    { color: 'limegreen', label: 'Clocked In' },
    { color: 'orange', label: 'Late' },
    { color: scheduledColor, label: 'Scheduled' },
  ];

  return (
    <div style={{ position: 'relative', marginBottom: '16px' }}>
      <div
        ref={mapRef}
        style={{ width: '100%', height: '400px', borderRadius: '8px' }}
      />
      <div style={{
        position: 'absolute',
        bottom: '12px',
        left: '12px',
        background: 'white',
        borderRadius: '6px',
        padding: '10px 12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
        fontSize: '13px',
        zIndex: 1
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '6px', color: '#333' }}>Status</div>
        {legendItems.map(item => (
          <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
            <span style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: item.color, flexShrink: 0 }}></span>
            <span style={{ color: '#444' }}>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
